import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import syncService from '../services/syncService';
import { isColumnRequired } from '../utils/conversionCodeWorkbook';
import Spinner from './shared/Spinner';
import ConfirmModal from './shared/ConfirmModal';
import './AddCorporateProfilePage.css';

// ONE ACCOUNTING CODE, EDITED.
//
// A PAGE, NOT A MODAL (STYLE.md "No Modals"): an add/edit form is a full page with an acp-card and a
// Cancel/Save footer, so it has a URL somebody can send to a colleague, a working back button, and room
// for thirty-four fields on a phone. A modal had none of those.
//
// WHY IT EXISTS AT ALL, given the workbook. The file is the right tool for mapping four hundred codes and
// stays the way that work is done. This is for the row somebody is looking at on the screen and can see
// is wrong: fixing that through the upload would mean downloading the file, changing one cell and
// replacing the entire map — every other row rewritten, and the sweep removing anything the re-uploaded
// file happened to miss — to correct one GL account.
//
// THE COLUMNS COME FROM THE SERVER, exactly as the table and the workbook do. There is one definition of
// what a code holds and it is the store's; a copy kept here would drift the first time a column was added.
//
// THE CODE ITSELF IS NOT EDITABLE. It is the row's identity — the document id — so a different code is a
// different row. Shown, never typed into.

const ConversionCodeEditPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { syncId, taskId, code } = useParams();
    const [searchParams] = useSearchParams();

    const [loading, setLoading] = useState(true);
    const [columns, setColumns] = useState([]);
    const [contextColumns, setContextColumns] = useState([]);
    const [record, setRecord] = useState(null);
    const [form, setForm] = useState({});
    const [context, setContext] = useState({});
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    // The codes either side of this one, in the order of the list that was on screen.
    const [siblings, setSiblings] = useState([]);
    // A move the person has asked for that would discard typed changes, held until they answer.
    const [pendingMove, setPendingMove] = useState(null);
    // What the row looked like when it loaded, so "has this been changed" is a comparison and not a guess.
    const [initial, setInitial] = useState({ form: {}, context: {} });

    // Back to the TASK, never to an intermediate list: the task page is the parent a person came from and
    // the one they expect after Cancel or Save. `from` is carried through so the task page opens on the
    // same tab it was on.
    const from = searchParams.get('from');
    const backTo = `/sync/${syncId}/task/${taskId}/configure${from ? `?from=${encodeURIComponent(from)}` : ''}`;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            // Searched by the code rather than fetched by id: the list endpoint already answers with the
            // columns beside the rows, so one call gives the record AND the definition of its fields.
            const res = await syncService.getConversionCodeRecords(syncId, taskId, { q: code, limit: 50 });
            const cols = Array.isArray(res?.columns) ? res.columns : [];
            const rows = Array.isArray(res?.codes) ? res.codes : [];
            // `q` is a contains-match, so pick the row whose code IS this one. Falling back to the row
            // the table handed over: the search is a wildcard on an uppercased code, which need not match
            // a code stored in another case, and "we could not search for it" is not "it is not there".
            const passed = location.state && location.state.codeRow;
            const found = rows.find(r => String(r.code) === String(code))
                || (passed && String(passed.code) === String(code) ? passed : null);
            setColumns(cols);
            setContextColumns(Array.isArray(res?.contextColumns) ? res.contextColumns : []);
            setRecord(found);
            if (found) {
                const next = {};
                for (const col of cols) next[col.field] = found[col.field] == null ? '' : String(found[col.field]);
                const ctx = { ...(found.context || {}) };
                setForm(next);
                setContext(ctx);
                setInitial({ form: next, context: ctx });
            }

            // THE ORDER COMES FROM THE LIST THAT WAS ON SCREEN. Walking with Previous/Next should follow
            // what the person was looking at — their search, their sort — so the table hands its order
            // over when it navigates. Only when there is none (a bookmarked link, or a reload, which
            // drops history state) is the whole list fetched to rebuild it.
            const passedSiblings = location.state && Array.isArray(location.state.siblings)
                ? location.state.siblings : null;
            if (passedSiblings && passedSiblings.length) {
                setSiblings(passedSiblings);
            } else {
                const all = await syncService.getConversionCodeRecords(syncId, taskId, { limit: 5000 });
                setSiblings((Array.isArray(all?.codes) ? all.codes : []).map(r => String(r.code)));
            }
        } catch (e) {
            setErrors({ submit: e.response?.data?.message || e.message || 'Could not load this code' });
        } finally {
            setLoading(false);
        }
    }, [syncId, taskId, code, location.state]);

    useEffect(() => { load(); }, [load]);

    // Where this code sits in that order, and what is either side of it. A code the list does not contain
    // (it was edited, then the search changed) simply has no neighbours rather than guessing at some.
    const position = siblings.indexOf(String(code));
    const previousCode = position > 0 ? siblings[position - 1] : null;
    const nextCode = position >= 0 && position < siblings.length - 1 ? siblings[position + 1] : null;

    const dirty = JSON.stringify({ form, context }) !== JSON.stringify(initial);

    const goTo = (target) => {
        if (!target) return;
        navigate(
            `/sync/${syncId}/task/${taskId}/code/${encodeURIComponent(target)}`
            + `${from ? `?from=${encodeURIComponent(from)}` : ''}`,
            // The order travels on, so the next page can move again without re-fetching it.
            { state: { siblings } },
        );
    };

    // TYPED CHANGES ARE NOT THROWN AWAY QUIETLY. Moving off a row that has been edited asks first —
    // somebody filling in a GL account and reaching for Next should not lose it to a misread button.
    const requestMove = (target) => {
        if (!target) return;
        if (dirty) { setPendingMove(target); return; }
        goTo(target);
    };

    const set = (field, value) => {
        setForm(f => ({ ...f, [field]: value }));
        setErrors(e => ({ ...e, [field]: undefined, submit: undefined }));
    };
    const setCtx = (key, value) => {
        setContext(c => ({ ...c, [key]: value }));
        setErrors(e => ({ ...e, submit: undefined }));
    };

    // The same rule the upload enforces, checked here so the message arrives before the round trip
    // rather than after it. The server checks it again regardless — a screen is not a boundary.
    const validate = () => {
        const e = {};
        for (const col of columns) {
            // Against the form AS IT STANDS, so changing CATEGORY_TYPE to payments clears the error on
            // CATEGORY_ID as you type rather than after a save that would have been refused.
            if (isColumnRequired(col, form) && col.field !== 'code' && !String(form[col.field] ?? '').trim()) {
                e[col.field] = `${col.header} is required`;
            }
        }
        return e;
    };

    // `after` decides where a successful save lands: back at the task (the ordinary Save, and what every
    // other form in the portal does) or on the next code, which is what makes walking a chart of them
    // bearable — mapping is one row after another, and returning to the table between each is the slow
    // part of the job.
    const handleSave = async (after = 'back') => {
        const e = validate();
        if (Object.keys(e).length) { setErrors(e); return; }
        setSaving(true);
        try {
            const fields = {};
            for (const col of columns) {
                if (col.field === 'code') continue;
                fields[col.field] = String(form[col.field] ?? '').trim();
            }
            // Blank context values are sent as blanks rather than dropped: the bag is replaced wholesale,
            // so leaving one out is how a person clears a field they no longer want.
            fields.context = {};
            for (const key of contextColumns) fields.context[key] = String(context[key] ?? '').trim();

            await syncService.updateConversionCodeRecord(syncId, taskId, code, fields);
            if (after === 'next' && nextCode) {
                // Saved, so there is nothing to discard and nothing to ask about.
                setInitial({ form: { ...form }, context: { ...context } });
                setSaving(false);
                goTo(nextCode);
                return;
            }
            navigate(backTo, { state: { ...(location.state || {}), codeSaved: code } });
        } catch (err) {
            setErrors({ submit: err.response?.data?.message || err.message || 'Could not save this code' });
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="acp-page"><div className="acp-card"><Spinner /></div></div>;
    }

    if (!record) {
        return (
            <div className="acp-page">
                <div className="acp-card">
                    <div className="acp-card-header"><h1 className="acp-title">Code not found</h1></div>
                    <div className="acp-divider" />
                    <div className="acp-body">
                        <p className="form-hint">
                            There is no code <strong>{code}</strong> on this task. It may have been removed by a
                            workbook upload since this page was opened.
                        </p>
                        {errors.submit && <div className="acp-error-msg">{errors.submit}</div>}
                    </div>
                    <div className="acp-footer">
                        <button className="acp-btn-cancel" onClick={() => navigate(backTo)}>Back to the task</button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="acp-page">
            <div className="acp-card">
                <div
                    className="acp-card-header"
                    style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem' }}
                >
                    <div>
                        <h1 className="acp-title">{record.code}</h1>
                        {record.description && <p className="form-hint" style={{ margin: 0 }}>{record.description}</p>}
                    </div>

                    {/* WALKING THE LIST. Mapping a chart of codes is one row after another, and going back
                        to the table between each one is the slow part of that job. The order is the order
                        of the list that was on screen, so a search narrows what these step through. */}
                    {siblings.length > 1 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
                            <button
                                className="acp-btn-cancel"
                                onClick={() => requestMove(previousCode)}
                                disabled={!previousCode || saving}
                                title={previousCode ? `Previous code (${previousCode})` : 'This is the first code'}
                            >
                                ‹ Previous
                            </button>
                            <span className="form-hint" style={{ whiteSpace: 'nowrap', margin: 0 }}>
                                {position >= 0
                                    ? `${position + 1} of ${siblings.length}`
                                    : `${siblings.length} codes`}
                            </span>
                            <button
                                className="acp-btn-cancel"
                                onClick={() => requestMove(nextCode)}
                                disabled={!nextCode || saving}
                                title={nextCode ? `Next code (${nextCode})` : 'This is the last code'}
                            >
                                Next ›
                            </button>
                        </div>
                    )}
                </div>

                <div className="acp-divider" />

                <div className="acp-body">
                    {/* WITHDRAWN IS SAID, NOT HIDDEN. The property has stopped returning this code; the
                        mapping against it is still editable, because the accounting work somebody did on
                        it is still the answer for the records that already carry it. */}
                    {record.still_returned === 'N' && (
                        <p className="form-hint" style={{ marginTop: 0 }}>
                            The source system no longer returns this code. Its mapping is kept and can still be
                            edited; a workbook upload that leaves it out will remove it.
                        </p>
                    )}

                    {columns.map(col => (
                        <div className="acp-field" key={col.field}>
                            <label className="acp-label">
                                {col.header}
                                {/* The asterisk follows the same rule as the check, or a field marked
                                    required would save happily empty — or worse, one left unmarked
                                    would refuse. */}
                                {isColumnRequired(col, form) && col.field !== 'code' && <span className="acp-required">*</span>}
                            </label>
                            {col.field === 'code' ? (
                                // Identity, shown and not editable — a different code is a different row.
                                <input className="acp-input" type="text" value={form.code || ''} disabled readOnly />
                            ) : (
                                <input
                                    className={`acp-input${errors[col.field] ? ' acp-input-error' : ''}`}
                                    type="text"
                                    value={form[col.field] ?? ''}
                                    onChange={e => set(col.field, e.target.value)}
                                />
                            )}
                            {errors[col.field] && <span className="acp-error-msg">{errors[col.field]}</span>}
                        </div>
                    ))}

                    {/* THE PROPERTY'S OWN WORKING COLUMNS. We never read these — they are carried so the
                        workbook a person downloads is the workbook they uploaded — but they are editable
                        here for the same reason the rest is: the row is in front of them. */}
                    {contextColumns.length > 0 && (
                        <>
                            <div className="acp-divider" />
                            <p className="form-hint" style={{ margin: '0 0 0.75rem' }}>
                                Additional columns, kept with the code and included in the workbook download.
                                They are not used by the sync.
                            </p>
                            {contextColumns.map(key => (
                                <div className="acp-field" key={key}>
                                    <label className="acp-label">{key}</label>
                                    <input
                                        className="acp-input"
                                        type="text"
                                        value={context[key] ?? ''}
                                        onChange={e => setCtx(key, e.target.value)}
                                    />
                                </div>
                            ))}
                        </>
                    )}
                </div>

                {errors.submit && <div className="acp-error-msg" style={{ margin: '0 0 1rem' }}>{errors.submit}</div>}
                <div className="acp-footer">
                    <button className="acp-btn-cancel" onClick={() => navigate(backTo)} disabled={saving}>
                        Cancel
                    </button>
                    <button className="acp-btn-create" onClick={() => handleSave('back')} disabled={saving}>
                        {saving ? 'Saving…' : 'Save'}
                    </button>
                    {nextCode && (
                        <button
                            className="acp-btn-create"
                            onClick={() => handleSave('next')}
                            disabled={saving}
                            title={`Save and open ${nextCode}`}
                        >
                            {saving ? 'Saving…' : 'Save & next'}
                        </button>
                    )}
                </div>
            </div>

            {/* Moving off an edited row asks first. Discarding typed work on a misread button is the one
                way this page could lose somebody's afternoon. */}
            {pendingMove && (
                <ConfirmModal
                    title="Discard your changes?"
                    message={`${record.code} has been edited and not saved. Moving to ${pendingMove} discards those changes.`}
                    confirmLabel="Discard and move"
                    danger
                    onConfirm={() => { const target = pendingMove; setPendingMove(null); goTo(target); }}
                    onCancel={() => setPendingMove(null)}
                />
            )}
        </div>
    );
};

export default ConversionCodeEditPage;
